import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ClipboardCheck, Plus, ScanLine, Check, FileDown, Search, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function fmtCur(n) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(n || 0);
}

export default function Inventory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isList = !id;
  const [list, setList] = useState([]);
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [showOnlyOpen, setShowOnlyOpen] = useState(false);
  const [showOnlyDiff, setShowOnlyDiff] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);
  const inputRef = useRef(null);
  const scanRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      if (isList) {
        const { data } = await api.get("/inventories");
        setList(data || []);
      } else {
        const { data } = await api.get(`/inventories/${id}`);
        setInv(data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const startInventory = async () => {
    try {
      const { data } = await api.post("/inventories", { title: null });
      toast.success("Inventur gestartet.");
      navigate(`/inventur/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    const ean = (scanRef.current?.value || "").trim();
    const qtyStr = (inputRef.current?.value || "").trim();
    if (!ean) {
      toast.error("EAN eingeben.");
      return;
    }
    if (!qtyStr) {
      toast.error("Ist-Menge eingeben.");
      return;
    }
    const item = inv.items.find((it) => (it.ean || "").trim() === ean);
    if (!item) {
      toast.error(`EAN ${ean} ist nicht in dieser Inventur.`);
      return;
    }
    try {
      await api.post(`/inventories/${id}/scan`, {
        article_id: item.article_id,
        actual_qty: Number(qtyStr),
      });
      toast.success(`✓ ${item.name}: ${qtyStr} Stk. erfasst.`);
      scanRef.current.value = "";
      inputRef.current.value = "";
      scanRef.current.focus();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const manualCount = async (item, qty) => {
    try {
      await api.post(`/inventories/${id}/scan`, {
        article_id: item.article_id,
        actual_qty: Number(qty),
      });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const finish = async (applyDiffs) => {
    try {
      const { data } = await api.post(`/inventories/${id}/finish?apply=${applyDiffs ? "true" : "false"}`);
      toast.success(`Inventur abgeschlossen — ${data.applied_count} Korrekturen angewendet.`);
      setFinishOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadPdf = async () => {
    const token = localStorage.getItem("lager_token");
    const res = await fetch(`${API}/inventories/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Inventur_${inv?.title?.replace(/\s+/g, "_") || id}.pdf`;
    a.click();
  };

  // ----- LIST VIEW -----
  if (isList) {
    return (
      <div className="max-w-[1100px] mx-auto space-y-6" data-testid="inventory-list-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
              STICHTAGS-INVENTUR
            </div>
            <h1 className="font-display text-4xl uppercase flex items-center gap-3">
              <ClipboardCheck className="w-8 h-8" /> Inventur
            </h1>
          </div>
          <Button
            onClick={startInventory}
            data-testid="start-inventory-button"
            className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm h-11 px-6 font-semibold tracking-wide"
          >
            <Plus className="w-4 h-4 mr-2" /> NEUE INVENTUR
          </Button>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          {loading ? (
            <div className="p-8 mono text-xs text-zinc-500 text-center">LADEN…</div>
          ) : list.length === 0 ? (
            <div className="p-12 text-center">
              <ClipboardCheck className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-sm text-zinc-600 mb-4">Noch keine Inventur durchgeführt.</p>
              <Button onClick={startInventory} className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm">
                <Plus className="w-4 h-4 mr-2" /> Erste Inventur starten
              </Button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-100">
                <tr>
                  <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">TITEL</th>
                  <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">STATUS</th>
                  <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">GESTARTET</th>
                  <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">ARTIKEL</th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => (
                  <tr
                    key={row.id}
                    className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                    onClick={() => navigate(`/inventur/${row.id}`)}
                    data-testid={`inventory-row-${row.id}`}
                  >
                    <td className="px-4 py-3 font-medium">{row.title}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 mono text-[10px] tracking-wider border ${
                          row.status === "finished"
                            ? "bg-green-100 text-green-800 border-green-300"
                            : "bg-yellow-100 text-yellow-800 border-yellow-300"
                        }`}
                      >
                        {row.status === "finished" ? "ABGESCHLOSSEN" : "OFFEN"}
                      </span>
                    </td>
                    <td className="px-4 py-3 mono text-xs text-zinc-600">
                      {row.started_at?.slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="px-4 py-3 text-right mono">{row.total_articles}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // ----- DETAIL VIEW -----
  if (loading || !inv) {
    return <div className="p-8 mono text-xs text-zinc-500 text-center">LADEN…</div>;
  }
  const counted = inv.items.filter((it) => it.ist !== null && it.ist !== undefined).length;
  const open = inv.items.length - counted;
  const discrepancies = inv.items.filter((it) => it.diff !== null && it.diff !== 0).length;
  const diffValue = inv.items.reduce((acc, it) => acc + (it.diff || 0) * (it.ek_price || 0), 0);

  const filtered = inv.items.filter((it) => {
    if (showOnlyOpen && (it.ist !== null && it.ist !== undefined)) return false;
    if (showOnlyDiff && (it.diff === null || it.diff === 0)) return false;
    if (filter) {
      const q = filter.toLowerCase();
      if (!(it.name || "").toLowerCase().includes(q) && !(it.ean || "").includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="inventory-detail-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <Link to="/inventur" className="mono text-[11px] tracking-[0.25em] text-zinc-500 hover:text-[#F97316]">
            ← INVENTUR-LISTE
          </Link>
          <h1 className="font-display text-3xl uppercase mt-2 flex items-center gap-3">
            <ClipboardCheck className="w-7 h-7" /> {inv.title}
          </h1>
          <div className="mono text-xs text-zinc-500 mt-1">
            Status:{" "}
            <span
              className={`font-bold ${
                inv.status === "finished" ? "text-green-700" : "text-yellow-700"
              }`}
            >
              {inv.status === "finished" ? "ABGESCHLOSSEN" : "OFFEN"}
            </span>
            {" · "}
            Erfasst {counted} / {inv.items.length} · Differenzen {discrepancies}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={downloadPdf}
            variant="outline"
            data-testid="inventory-pdf-button"
            className="rounded-sm border-zinc-400 h-11"
          >
            <FileDown className="w-4 h-4 mr-2" /> PDF herunterladen
          </Button>
          {inv.status === "open" && (
            <Button
              onClick={() => setFinishOpen(true)}
              data-testid="inventory-finish-button"
              className="bg-black hover:bg-zinc-800 text-white rounded-sm h-11 px-6 font-semibold tracking-wide"
            >
              <Check className="w-4 h-4 mr-2" /> INVENTUR ABSCHLIESSEN
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-zinc-200 rounded-sm p-4">
          <div className="mono text-[10px] tracking-[0.2em] text-zinc-500">ARTIKEL GESAMT</div>
          <div className="font-display text-3xl mt-1">{inv.items.length}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-4">
          <div className="mono text-[10px] tracking-[0.2em] text-zinc-500">ERFASST</div>
          <div className="font-display text-3xl mt-1 text-green-700">{counted}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-4">
          <div className="mono text-[10px] tracking-[0.2em] text-zinc-500">OFFEN</div>
          <div className="font-display text-3xl mt-1 text-yellow-700">{open}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-4">
          <div className="mono text-[10px] tracking-[0.2em] text-zinc-500">DIFF.-WERT</div>
          <div
            className={`font-display text-3xl mt-1 ${
              diffValue < 0 ? "text-red-600" : diffValue > 0 ? "text-green-700" : ""
            }`}
          >
            {fmtCur(diffValue)}
          </div>
        </div>
      </div>

      {inv.status === "open" && (
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-3">
            SCAN-MODUS · EAN + IST-BESTAND ERFASSEN
          </div>
          <form onSubmit={handleScan} className="grid grid-cols-1 md:grid-cols-[1fr_120px_auto] gap-3 items-end">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">EAN scannen</Label>
              <div className="relative mt-1.5">
                <ScanLine className="absolute left-3 top-3 w-4 h-4 text-zinc-500 pointer-events-none" />
                <Input
                  ref={scanRef}
                  autoFocus
                  data-testid="inventory-ean-input"
                  placeholder="EAN…"
                  className="pl-9 h-11 font-mono-num rounded-sm border-zinc-400"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Ist-Bestand</Label>
              <Input
                ref={inputRef}
                type="number"
                min={0}
                data-testid="inventory-qty-input"
                className="mt-1.5 h-11 font-mono-num rounded-sm border-zinc-400 text-center text-lg"
              />
            </div>
            <Button
              type="submit"
              data-testid="inventory-scan-submit"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-sm h-11 px-6 font-semibold tracking-wide"
            >
              <Check className="w-4 h-4 mr-2" /> ERFASSEN
            </Button>
          </form>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-zinc-200">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-3 w-4 h-4 text-zinc-500 pointer-events-none" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter nach Name / EAN…"
              data-testid="inventory-filter-input"
              className="pl-9 h-10 rounded-sm border-zinc-400"
            />
          </div>
          <label className="text-xs flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyOpen}
              onChange={(e) => setShowOnlyOpen(e.target.checked)}
              data-testid="inventory-only-open"
            />
            Nur offene
          </label>
          <label className="text-xs flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyDiff}
              onChange={(e) => setShowOnlyDiff(e.target.checked)}
              data-testid="inventory-only-diff"
            />
            Nur Differenzen
          </label>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-100">
              <tr>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">ARTIKEL</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">EAN</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">REGAL</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">SOLL</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">IST</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">DIFF.</th>
                {inv.status === "open" && <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">ZÄHLEN</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr
                  key={it.article_id}
                  className={`border-t border-zinc-100 ${
                    it.diff && it.diff !== 0 ? "bg-yellow-50" : ""
                  }`}
                >
                  <td className="px-4 py-2 font-medium">{it.name}</td>
                  <td className="px-4 py-2 mono text-xs text-zinc-500">{it.ean || "—"}</td>
                  <td className="px-4 py-2 mono text-xs">{it.shelf_label || "—"}</td>
                  <td className="px-4 py-2 text-right mono font-bold">{it.soll}</td>
                  <td className="px-4 py-2 text-right mono">
                    {it.ist == null ? <span className="text-zinc-400">—</span> : it.ist}
                  </td>
                  <td
                    className={`px-4 py-2 text-right mono font-bold ${
                      it.diff > 0
                        ? "text-green-700"
                        : it.diff < 0
                        ? "text-red-700"
                        : ""
                    }`}
                  >
                    {it.diff == null ? "" : it.diff > 0 ? `+${it.diff}` : it.diff}
                  </td>
                  {inv.status === "open" && (
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        defaultValue={it.ist ?? ""}
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v !== "" && Number(v) !== it.ist) {
                            manualCount(it, v);
                          }
                        }}
                        data-testid={`inventory-manual-${it.article_id}`}
                        className="border border-zinc-300 rounded-sm px-2 py-1 w-20 text-right mono"
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={finishOpen} onOpenChange={setFinishOpen}>
        <AlertDialogContent className="rounded-sm" data-testid="inventory-finish-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Inventur abschließen</AlertDialogTitle>
            <AlertDialogDescription>
              {open > 0
                ? `${open} Artikel sind noch NICHT gezählt. Beim Abschluss werden diese als unverändert behandelt.`
                : `Alle ${inv.items.length} Artikel wurden gezählt.`}
              <br />
              <br />
              Mit „Differenzen anwenden" werden die {discrepancies} Differenzen direkt im Bestand
              korrigiert (mit Buchung „Inventur" im Bewegungs-Log).
              <br />
              <br />
              <strong>Diff-Wert (EK):</strong> {fmtCur(diffValue)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {open > 0 && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-sm p-3 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 text-yellow-700 shrink-0" />
              <span>
                Nicht-gezählte Artikel bleiben unverändert. Empfehlung: erst zu Ende zählen.
              </span>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => finish(false)}
              data-testid="inventory-finish-noapply"
              className="rounded-sm border-zinc-400"
            >
              Nur protokollieren
            </Button>
            <AlertDialogAction
              onClick={() => finish(true)}
              data-testid="inventory-finish-apply"
              className="bg-[#F97316] hover:bg-[#EA580C] rounded-sm"
            >
              Differenzen anwenden
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
